import { normalizeRedeemCodeStatus } from "./redeemCodeStatusFilters";

/**
 * Reports whether a customer LunaKey row comes from a reseller-managed link
 * and must stay read-only in admin views.
 *
 * @param {{ managedBy?: string }|null|undefined} redeemCode
 * @returns {boolean}
 */
export const isResellerManagedLunaKey = (redeemCode) =>
  redeemCode?.managedBy === "reseller";

/**
 * @param {{ managedBy?: string }|null|undefined} redeemCode
 * @returns {"Reseller Managed"|"Customer Managed"}
 */
export const getLunaKeyManagedLabel = (redeemCode) =>
  isResellerManagedLunaKey(redeemCode)
    ? "Reseller Managed"
    : "Customer Managed";

/**
 * Builds the admin LunaKeys table rows from the customer's own codes and the
 * reseller LunaKeys that reach the customer through managed playlist links.
 * The owned list itself is never modified so invoices and other consumers
 * keep treating only owned codes as the customer's.
 *
 * @param {Array<object>} redeemCodes
 * @param {Array<object>} linkedRedeemCodes
 * @returns {Array<object>}
 */
export const mergeCustomerLunaKeys = (
  redeemCodes = [],
  linkedRedeemCodes = []
) => {
  const linked = (linkedRedeemCodes || []).map((code) => ({
    ...code,
    managedBy: "reseller",
  }));
  const linkedIds = new Set(
    linked.map((code) => code.redeem_code_id).filter(Boolean)
  );
  const owned = (redeemCodes || [])
    .filter((code) => !linkedIds.has(code.redeem_code_id))
    .map((code) => ({ ...code, managedBy: code.managedBy || "customer" }));

  return [...owned, ...linked];
};

const countByStatus = (codes, status) =>
  codes.filter((code) => normalizeRedeemCodeStatus(code?.status) === status)
    .length;

/**
 * Counts claimed and unclaimed LunaKeys for the customer summary cards.
 * Reseller-managed LunaKeys count as claimed, while the unclaimed count only
 * ever reflects LunaKeys the customer owns.
 *
 * @param {Array<object>} redeemCodes
 * @param {Array<object>} linkedRedeemCodes
 * @returns {{ claimed: number, unclaimed: number }}
 */
export const countCustomerLunaKeyStatuses = (
  redeemCodes = [],
  linkedRedeemCodes = []
) => ({
  claimed: countByStatus(
    mergeCustomerLunaKeys(redeemCodes, linkedRedeemCodes),
    "CLAIMED"
  ),
  unclaimed: countByStatus(redeemCodes || [], "UNCLAIMED"),
});

const dateMatchesSearch = (value, searchTerm) =>
  Boolean(value) &&
  new Date(value).toLocaleDateString().toLowerCase().includes(searchTerm);

/**
 * Free-text filter for the admin LunaKeys table. It matches the code, status,
 * type, active and auto-renew flags, and dates. The managed-by label is
 * deliberately not matched: "Reseller Managed" and "Customer Managed" would
 * make short terms such as "man" or "cust" match every row.
 *
 * @param {object} redeemCode
 * @param {string} searchTerm - Lowercased, trimmed search text.
 * @returns {boolean}
 */
export const lunaKeyMatchesSearch = (redeemCode, searchTerm) => {
  const code = redeemCode || {};
  const metadata = code.metadata || {};

  if (code.code?.toLowerCase().includes(searchTerm)) return true;
  if (code.status?.toLowerCase().includes(searchTerm)) return true;
  if (code.redeem_type?.toLowerCase().includes(searchTerm)) return true;

  if (code.active && "active".includes(searchTerm)) return true;
  if (!code.active && "inactive".includes(searchTerm)) return true;

  if (
    metadata.autoRenew &&
    ("autorenew".includes(searchTerm) || "enabled".includes(searchTerm))
  )
    return true;
  if (!metadata.autoRenew && "disabled".includes(searchTerm)) return true;

  return (
    dateMatchesSearch(code.created_at, searchTerm) ||
    dateMatchesSearch(code.expires_at, searchTerm) ||
    dateMatchesSearch(code.date_redeemed, searchTerm)
  );
};
